import React, { useState, useMemo, useCallback } from 'react';
import { applyKitchenEodDiscount } from '../../lib/kitchenEodDiscount';

// POS-specific modifier picker. Behavioural parity with the customer app's
// MenuItem.js modifier flow (same conditional-hide rules, same required
// checks, same surcharge math) but with a large-button UI that fills a
// modal — designed for a touch counter, not a phone.
//
// Optional extras that are a single choice ("Add chips", "Extra hot")
// toggle on one tap. Multi-option extras are shown immediately so staff
// do not have to expand a group first.

const NONE_SENTINEL = '__none__';

function parseHiddenWhenOptionIds(raw) {
  if (raw === null || raw === undefined || raw === '') return [];
  let arr = raw;
  if (typeof arr === 'string') {
    try { arr = JSON.parse(arr); } catch { return []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => Number(x)).filter((x) => Number.isInteger(x) && x > 0);
}

function isGenericOptionName(name) {
  return /^(yes|no|add|on|off)$/i.test(String(name || '').trim());
}

function clampQty(n) {
  const q = Number(n);
  if (!Number.isFinite(q)) return 1;
  return Math.min(99, Math.max(1, Math.floor(q)));
}

export default function PosModifierModal({
  item,
  onConfirm,
  onCancel,
  initialSelections = null,
  initialQuantity = 1,
  confirmLabel = 'Add to cart',
  eodOn = false,
}) {
  const [selections, setSelections] = useState(() => (
    initialSelections && typeof initialSelections === 'object' ? { ...initialSelections } : {}
  ));
  const [quantity, setQuantity] = useState(() => clampQty(initialQuantity));

  const selectedOptionIds = useMemo(() => {
    const ids = new Set();
    for (const g of item.modifier_groups || []) {
      const sel = selections[g.name];
      if (!sel || sel === NONE_SENTINEL) continue;
      const opt = g.options.find((o) => o.name === sel);
      if (opt) ids.add(opt.id);
    }
    return ids;
  }, [item.modifier_groups, selections]);

  const isGroupHidden = useCallback((group) => {
    const triggers = parseHiddenWhenOptionIds(group.hidden_when_option_ids);
    if (triggers.length === 0) return false;
    return triggers.some((id) => selectedOptionIds.has(id));
  }, [selectedOptionIds]);

  const visibleGroups = useMemo(() => (
    (item.modifier_groups || []).filter((g) => !isGroupHidden(g))
  ), [item.modifier_groups, isGroupHidden]);

  const requiredGroups = useMemo(
    () => visibleGroups.filter((g) => g.required),
    [visibleGroups]
  );
  const optionalGroups = useMemo(
    () => visibleGroups.filter((g) => !g.required),
    [visibleGroups]
  );

  const surcharge = useMemo(() => {
    let total = 0;
    for (const g of visibleGroups) {
      const sel = selections[g.name];
      if (sel && sel !== NONE_SENTINEL) {
        const opt = g.options.find((o) => o.name === sel);
        if (opt) total += opt.surcharge || 0;
      }
    }
    return total;
  }, [visibleGroups, selections]);

  const unitPrice = applyKitchenEodDiscount(item.price + surcharge, item, eodOn);
  const linePrice = unitPrice * quantity;

  const allRequiredSelected = requiredGroups.every(
    (g) => selections[g.name] && selections[g.name] !== NONE_SENTINEL
  );

  const handleSelect = (group, optionName) => {
    setSelections((prev) => {
      // Optional extras toggle off if staff tap the already-selected choice.
      if (!group.required && prev[group.name] === optionName) {
        const next = { ...prev };
        delete next[group.name];
        return next;
      }
      return { ...prev, [group.name]: optionName };
    });
  };

  const handleConfirm = () => {
    const visibleNames = new Set(visibleGroups.map((g) => g.name));
    const cleanOptions = {};
    for (const [k, v] of Object.entries(selections)) {
      if (!visibleNames.has(k)) continue;
      if (v && v !== NONE_SENTINEL) cleanOptions[k] = v;
    }
    onConfirm({
      item,
      options: cleanOptions,
      surcharge,
      quantity,
    });
  };

  const hasAnyOptionsBlocked = requiredGroups.some((g) => (
    g.track_stock
    && g.options.every((o) => o.stock_quantity !== null
      && o.stock_quantity !== undefined
      && o.stock_quantity <= 0)
  ));

  const optionOutOfStock = (group, opt) => (
    group.track_stock
    && opt.stock_quantity !== null
    && opt.stock_quantity !== undefined
    && opt.stock_quantity <= 0
  );

  const renderOptionButtons = (group) => (
    <div className="pos-mod-options">
      {group.options.map((opt) => {
        const outOfStock = optionOutOfStock(group, opt);
        const isSelected = selections[group.name] === opt.name;
        return (
          <button
            key={opt.id}
            type="button"
            className={`pos-mod-option-btn ${isSelected ? 'selected' : ''}`}
            disabled={outOfStock}
            onClick={() => handleSelect(group, opt.name)}
          >
            <span className="pos-mod-option-name">{opt.name}{outOfStock ? ' (sold out)' : ''}</span>
            {opt.surcharge > 0 && (
              <span className="pos-mod-option-surcharge">+${opt.surcharge.toFixed(2)}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  const renderSingleOptionalToggle = (group) => {
    const opt = group.options[0];
    const outOfStock = optionOutOfStock(group, opt);
    const selected = selections[group.name] === opt.name;
    const label = isGenericOptionName(opt.name) ? group.name : opt.name;
    return (
      <button
        type="button"
        className={`pos-mod-optional-toggle${selected ? ' is-on' : ''}`}
        disabled={outOfStock}
        onClick={() => handleSelect(group, opt.name)}
        aria-pressed={selected}
      >
        <span>{selected ? label : `+ ${label}`}</span>
        <span className="pos-mod-optional-meta">
          {outOfStock
            ? 'Sold out'
            : opt.surcharge > 0
              ? `+$${opt.surcharge.toFixed(2)}`
              : selected
                ? 'Added — tap to remove'
                : 'Tap to add'}
        </span>
      </button>
    );
  };

  return (
    <div className="pos-modal-backdrop" onClick={onCancel}>
      <div className="pos-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="pos-modal-title">{item.name}</h2>
        <div className="pos-modal-subtitle">
          Base ${applyKitchenEodDiscount(item.price, item, eodOn).toFixed(2)}{item.description ? ` — ${item.description}` : ''}
        </div>

        {requiredGroups.map((group) => (
          <div key={group.id} className="pos-mod-group">
            <div className="pos-mod-group-label">
              {group.name}<span className="pos-mod-required-star"> *</span>
            </div>
            {renderOptionButtons(group)}
          </div>
        ))}

        {optionalGroups.length > 0 && (
          <div className="pos-mod-optional-section">
            <div className="pos-mod-optional-heading">Optional extras — tap to add</div>
            {optionalGroups.map((group) => {
              const single = group.options.length === 1;
              return (
                <div key={group.id} className="pos-mod-group pos-mod-group-optional">
                  {single ? (
                    renderSingleOptionalToggle(group)
                  ) : (
                    <>
                      <div className="pos-mod-group-label">{group.name}</div>
                      {renderOptionButtons(group)}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="pos-mod-qty-row">
          <span className="pos-mod-qty-label">Qty</span>
          <button
            type="button"
            className="pos-cart-qty-btn"
            aria-label="Decrease quantity"
            onClick={() => setQuantity((q) => clampQty(q - 1))}
          >
            &minus;
          </button>
          <span className="pos-mod-qty-value">{quantity}</span>
          <button
            type="button"
            className="pos-cart-qty-btn"
            aria-label="Increase quantity"
            onClick={() => setQuantity((q) => clampQty(q + 1))}
          >
            +
          </button>
          <span className="pos-mod-live-price">${linePrice.toFixed(2)}</span>
        </div>

        <div className="pos-modal-actions">
          <button className="pos-modal-btn pos-modal-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="pos-modal-btn pos-modal-btn-primary"
            disabled={!allRequiredSelected || hasAnyOptionsBlocked}
            onClick={handleConfirm}
          >
            {hasAnyOptionsBlocked ? 'All options sold out' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
